/**
 * What the assigned scanners heard.
 *
 * Not a waterfall. One row per emitter, places after clustering so GPS
 * jitter is not "several locations". A live mission starts a digest and
 * refreshes it every five minutes; Scan still asks for one now and
 * restarts that clock. Add to watchlist is the only write.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Collapse, InputBase, Tooltip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import SearchIcon from '@mui/icons-material/Search';
import {
  MissionSignalCluster,
  MissionSignalEmitter,
  MissionSignalSurvey,
  promoteMissionSignal,
  surveyMissionSignals,
} from '../../services/missionApi';
import { formatWhen } from './format';
import { Action, MUTED, PAPER, TEAL } from './missionLiveParts';
import DockStatStrip from './DockStatStrip';
import { DockTabHelp } from './DockTabHelp';
import { emitterMatchesQuery } from './signalView';
import WifiIcon from '@mui/icons-material/Wifi';
import BluetoothIcon from '@mui/icons-material/Bluetooth';
import RadarIcon from '@mui/icons-material/CellTower';
import VisibilityIcon from '@mui/icons-material/Visibility';

const EDGE = '#262b33';
const GOLD = '#f5b942';
/** Wi-Fi wears COP's blue; BLE keeps mission teal, matching the waterfall. */
const WIFI_BLUE = '#64b5f6';

const POLL_MS = 5 * 60 * 1000;

export interface MissionSignalsTabProps {
  missionId: number;
  /** Mission is running — start a digest and refresh it on the clock. */
  live?: boolean;
  hasAreas?: boolean;
  onFocus?: (longitude: number, latitude: number) => void;
  onCount?: (count: number | null) => void;
}

const MissionSignalsTab: React.FC<MissionSignalsTabProps> = ({
  missionId,
  live = false,
  onFocus,
  onCount,
}) => {
  const [survey, setSurvey] = useState<MissionSignalSurvey | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [promoting, setPromoting] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [pollKey, setPollKey] = useState(0);
  const busy = useRef(false);

  useEffect(() => { setQuery(''); }, [missionId]);

  const scan = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setLoading(true);
    setError(null);
    try {
      const next = await surveyMissionSignals(missionId, 'run');
      setSurvey(next);
      onCount?.(next.multi_count);
    } catch {
      setError('Could not read what the scanners heard');
      setSurvey(null);
      onCount?.(null);
    } finally {
      busy.current = false;
      setLoading(false);
    }
  }, [missionId, onCount]);

  const requestScan = () => setPollKey((n) => n + 1);

  useEffect(() => {
    if (!live) return undefined;
    scan();
    const timer = window.setInterval(scan, POLL_MS);
    return () => window.clearInterval(timer);
  }, [live, missionId, pollKey, scan]);

  const heard = survey?.emitters || [];
  const rows = useMemo(
    () => heard.filter((emitter) => emitterMatchesQuery(emitter, query)),
    [heard, query],
  );

  const promote = async (emitter: MissionSignalEmitter) => {
    setPromoting(emitter.normalized);
    try {
      await promoteMissionSignal(missionId, {
        identifier: emitter.identifier,
        signal_type: emitter.kind === 'ble' ? 'bluetooth' : 'wifi',
        identifier_type: emitter.kind === 'wifi' ? 'bssid' : 'mac',
        name: emitter.name || undefined,
        reason: `Promoted from the Signals survey (${emitter.places} places)`,
      });
      setSurvey((current) => current && {
        ...current,
        emitters: current.emitters.map((e) => (
          e.normalized === emitter.normalized ? { ...e, watchlisted: true } : e
        )),
      });
    } catch {
      setError('Could not add that to the watchlist');
    } finally {
      setPromoting(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          Heard around us
        </Typography>
        <DockTabHelp tab="signals" />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Nearby GPS jitter is one place.
        {live
          ? ' Updates every five minutes. Scan now for a fresh digest.'
          : ' Updates every five minutes once the mission is running.'}
      </Typography>

      {survey && !survey.reason && (
        <DockStatStrip
          items={[
            { label: 'Heard', value: survey.heard, color: MUTED, icon: <RadarIcon /> },
            { label: 'Shown', value: rows.length, color: PAPER, icon: <VisibilityIcon /> },
            { label: 'Multi', value: survey.multi_count, color: GOLD },
            { label: 'Wi-Fi', value: survey.emitters.filter((e) => e.kind === 'wifi').length, color: WIFI_BLUE, icon: <WifiIcon /> },
            { label: 'BLE', value: survey.emitters.filter((e) => e.kind === 'ble').length, color: TEAL, icon: <BluetoothIcon /> },
            { label: 'Own kit', value: survey.emitters.filter((e) => e.own_kit).length, color: TEAL },
            { label: 'Watchlist', value: survey.emitters.filter((e) => e.watchlisted).length, color: '#ef5350' },
            { label: 'Observers', value: survey.observers.length, color: '#ce93d8' },
          ]}
        />
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
        <Action filled disabled={loading} onClick={live ? requestScan : scan}>
          {loading ? 'Scanning…' : survey ? 'Scan now' : 'Scan'}
        </Action>
        {loading && <CircularProgress size={12} sx={{ color: PAPER }} />}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
            height: 26,
            px: 0.75,
            borderRadius: 1,
            border: `1px solid ${EDGE}`,
            bgcolor: 'rgba(255,255,255,0.04)',
          }}
        >
          <SearchIcon sx={{ fontSize: 14, color: MUTED, mr: 0.5, flexShrink: 0 }} />
          <InputBase
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name or MAC"
            sx={{
              flex: 1,
              fontSize: '0.7rem',
              color: '#e8ebee',
              '& input': { p: 0, '&::placeholder': { color: MUTED, opacity: 1 } },
            }}
          />
        </Box>
      </Box>

      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 0.75 }}>
          {error}
        </Typography>
      )}

      {!survey && !loading && (
        <Typography variant="caption" color="text.secondary">
          {live
            ? 'Waiting on the first digest.'
            : 'Nothing scanned yet. The badge stays empty until you ask or the mission goes live.'}
        </Typography>
      )}

      {survey && survey.reason && rows.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          {survey.reason === 'no RF observers assigned'
            ? 'No RF observers assigned. A follower cannot be heard.'
            : survey.reason === 'no watched areas'
              ? 'No areas watched. Edit the mission to pick them.'
              : survey.reason}
        </Typography>
      )}

      {survey && !survey.reason && rows.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          {query.trim()
            ? `Nothing matches “${query.trim()}”.`
            : 'Nothing heard in this window.'}
        </Typography>
      )}

      {rows.map((emitter) => (
        <SignalRow
          key={emitter.normalized}
          emitter={emitter}
          promoting={promoting === emitter.normalized}
          onFocus={onFocus}
          onPromote={promote}
        />
      ))}
    </Box>
  );
};

/**
 * One tick per distinct place, capped so a beacon heard everywhere does not
 * push the row apart. Two or more light gold — the whole reason to look.
 */
const PlaceTicks: React.FC<{ places: number }> = ({ places }) => {
  const shown = Math.min(Math.max(places, 1), 8);
  const multi = places >= 2;
  const tone = multi ? GOLD : MUTED;
  return (
    <Tooltip title={places === 1 ? 'Heard in one place' : `Heard in ${places} places`}>
      <Box
        aria-label={places === 1 ? 'Heard in one place' : `Heard in ${places} places`}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}
      >
        {Array.from({ length: shown }).map((_, i) => (
          <Box
            key={i}
            sx={{
              width: 4,
              height: multi ? 11 : 8,
              borderRadius: 0.35,
              bgcolor: tone,
              opacity: multi ? 0.55 + (0.45 * (i + 1)) / shown : 0.45,
            }}
          />
        ))}
        {places > shown && (
          <Typography component="span" sx={{ fontSize: '0.56rem', color: tone, fontWeight: 700, ml: 0.15 }}>
            +{places - shown}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

const SignalRow: React.FC<{
  emitter: MissionSignalEmitter;
  promoting: boolean;
  onFocus?: (longitude: number, latitude: number) => void;
  onPromote: (emitter: MissionSignalEmitter) => void;
}> = ({ emitter, promoting, onFocus, onPromote }) => {
  const [open, setOpen] = useState(false);
  const title = emitter.name || emitter.identifier;
  const canFocus = emitter.longitude != null && emitter.latitude != null;
  const kindTone = emitter.kind === 'ble' ? TEAL : WIFI_BLUE;
  const clusters = useMemo(
    () => [...(emitter.clusters || [])].sort((a, b) => b.sightings - a.sightings),
    [emitter.clusters],
  );
  const expandable = clusters.length > 1;
  return (
    <Box
      sx={{
        py: 0.65,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
        <Box
          aria-hidden
          sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: kindTone, flexShrink: 0 }}
        />
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#e8ebee', flex: 1, minWidth: 0 }}
          noWrap
        >
          {title}
        </Typography>
        <PlaceTicks places={emitter.places} />
        {emitter.own_kit && <QuietChip label="own kit" />}
        {emitter.watchlisted && <QuietChip label="watchlisted" tone={GOLD} />}
        {canFocus && onFocus && (
          <IconAction title="Show where" onClick={() => onFocus(emitter.longitude!, emitter.latitude!)}>
            <MyLocationIcon sx={{ fontSize: 14 }} />
          </IconAction>
        )}
        <IconAction
          title={emitter.watchlisted ? 'Already watchlisted' : 'Add to watchlist'}
          disabled={promoting || emitter.watchlisted}
          onClick={() => onPromote(emitter)}
        >
          <PlaylistAddIcon sx={{ fontSize: 15 }} />
        </IconAction>
      </Box>
      <Box
        role={expandable ? 'button' : undefined}
        tabIndex={expandable ? 0 : undefined}
        onClick={expandable ? () => setOpen((v) => !v) : undefined}
        onKeyDown={(e) => {
          if (expandable && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.6,
          pl: '13px',
          cursor: expandable ? 'pointer' : 'default',
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem' }}>
          {emitter.kind === 'ble' ? 'BLE' : 'Wi-Fi'}
          {` · ${emitter.places} place${emitter.places === 1 ? '' : 's'}`}
          {` · ${emitter.sightings} sighting${emitter.sightings === 1 ? '' : 's'}`}
          {emitter.last_seen ? ` · ${formatWhen(emitter.last_seen)}` : ''}
        </Typography>
        {expandable && (
          <ExpandMoreIcon
            sx={{
              fontSize: 14,
              color: MUTED,
              ml: 'auto',
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform .15s',
            }}
          />
        )}
      </Box>
      {expandable && (
        <Collapse in={open}>
          <Box sx={{ pl: '13px', pt: 0.4 }}>
            {clusters.map((cluster, i) => (
              <ClusterRow key={i} index={i} cluster={cluster} onFocus={onFocus} />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const ClusterRow: React.FC<{
  index: number;
  cluster: MissionSignalCluster;
  onFocus?: (longitude: number, latitude: number) => void;
}> = ({ index, cluster, onFocus }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.6,
      py: 0.2,
      borderLeft: `2px solid ${EDGE}`,
      pl: 0.85,
      mb: 0.1,
    }}
  >
    <Typography variant="caption" sx={{ fontSize: '0.62rem', color: MUTED }}>
      place {index + 1}
      {` · ${cluster.sightings} sighting${cluster.sightings === 1 ? '' : 's'}`}
    </Typography>
    {onFocus && (
      <IconAction title="Show this place" onClick={() => onFocus(cluster.longitude, cluster.latitude)}>
        <MyLocationIcon sx={{ fontSize: 12 }} />
      </IconAction>
    )}
  </Box>
);

const QuietChip: React.FC<{ label: string; tone?: string }> = ({ label, tone }) => (
  <Box
    component="span"
    sx={{
      height: 15,
      px: 0.5,
      borderRadius: 0.4,
      fontSize: '0.55rem',
      fontWeight: 700,
      color: tone || TEAL,
      border: '1px solid',
      borderColor: tone || TEAL,
    }}
  >
    {label}
  </Box>
);

const IconAction: React.FC<{
  title: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactElement;
}> = ({ title, disabled, onClick, children }) => (
  <Tooltip title={title}>
    <Box
      component="span"
      role="button"
      tabIndex={disabled ? undefined : 0}
      onClick={() => { if (!disabled) onClick(); }}
      onKeyDown={(e) => { if (!disabled && e.key === 'Enter') onClick(); }}
      sx={{
        display: 'flex',
        cursor: disabled ? 'default' : 'pointer',
        color: disabled ? 'text.disabled' : 'text.secondary',
        '&:hover': { color: disabled ? 'text.disabled' : GOLD },
      }}
    >
      {children}
    </Box>
  </Tooltip>
);

export default MissionSignalsTab;
